import type React from 'react';
import { Alert, Box, Button, CloseButton, Divider, Modal } from '@zillow/constellation-10';
import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import type { ClickstreamTriggerObjectOnboardingScreen } from '../analytics/events';
import { emitOnboardingWizardNextEvent } from '../analytics/events';

export interface WizardNavigationProps {
  goToNextScreen: () => void;
  goToPreviousScreen: () => void;
  goToScreen: (screenIndex: number) => void;
  currentScreen: number;
  totalScreens: number;
  isFirstScreen: boolean;
  isLastScreen: boolean;
}

export interface WizardScreen {
  id?: string; // Unique identifier for URL navigation
  header?: React.ReactNode;
  body: React.ReactNode;
  footer?: React.ReactNode | ((navigation: WizardNavigationProps) => React.ReactNode);
  backButtonText?: string;
  nextButtonText?: string;
  isNextEnabled?: boolean | (() => boolean);
  isLoading?: boolean; // Show loading spinner on next button
  showFooter?: boolean;
  nextRoute?: string;
  nextAction?: () => void; // Custom action to execute instead of default navigation
  clickstreamTriggerObject?: ClickstreamTriggerObjectOnboardingScreen;
}

export interface WizardWrapperProps {
  screens: Array<WizardScreen>;
  defaultHeader?: React.ReactNode;
  defaultBackButtonText?: string;
  defaultNextButtonText?: string;
  initialScreen?: number;
  error?: React.ReactNode | null;
  onDismissError?: () => void;
}

export default function WizardWrapper({
  screens,
  defaultHeader,
  defaultBackButtonText = 'Back',
  defaultNextButtonText = 'Next',
  initialScreen = 0,
  error,
  onDismissError,
}: WizardWrapperProps) {
  const router = useRouter();

  // Derive currentScreen from URL - memoized to prevent flash during hydration
  // Always prioritize actual URL over router.query for consistency
  const stepFromQuery = useMemo(() => {
    if (typeof window === 'undefined') return undefined;

    // Read directly from the URL to avoid Next.js router hydration delays
    const urlParams = new URLSearchParams(window.location.search);
    const stepFromUrl = urlParams.get('step');

    // If router is ready and has a step, use that (for programmatic navigation)
    // Otherwise use the URL step (for direct visits and initial load)
    if (router.isReady && router.query.step) {
      return router.query.step as string;
    }

    return stepFromUrl || undefined;
  }, [router.isReady, router.query.step]);

  const currentScreen = useMemo(() => {
    if (!stepFromQuery) return initialScreen;

    const screenIndex = screens.findIndex((screen) => screen.id === stepFromQuery);
    return screenIndex >= 0 ? screenIndex : initialScreen;
  }, [stepFromQuery, screens, initialScreen]);

  const currentScreenData = screens[currentScreen];
  const headerToShow = currentScreenData?.header ?? defaultHeader;

  const updateUrl = (screenIndex: number) => {
    const screen = screens[screenIndex];

    if (screen?.id) {
      router.push(
        {
          pathname: router.pathname,
          query: { ...router.query, step: screen.id },
        },
        undefined,
        { shallow: true },
      );
    } else {
      const { step: _step, ...restQuery } = router.query;
      router.push(
        {
          pathname: router.pathname,
          query: restQuery,
        },
        undefined,
        { shallow: true },
      );
    }
  };

  const goToNextScreen = () => {
    // Emit analytics event if clickstream trigger is provided
    if (currentScreenData?.clickstreamTriggerObject) {
      emitOnboardingWizardNextEvent(
        currentScreenData.clickstreamTriggerObject as ClickstreamTriggerObjectOnboardingScreen,
      );
    }

    // Check if current screen has a custom route
    if (currentScreenData?.nextRoute) {
      router.push(currentScreenData.nextRoute as Parameters<typeof router.push>[0]);
      return;
    }

    // Check if current screen has a custom nextAction
    if (currentScreenData?.nextAction) {
      currentScreenData.nextAction();
      return;
    }

    // Default behavior: go to next screen
    if (currentScreen < screens.length - 1) {
      const nextScreen = currentScreen + 1;
      updateUrl(nextScreen);
    }
  };

  const goToPreviousScreen = () => {
    if (currentScreen > 0) {
      const prevScreen = currentScreen - 1;
      updateUrl(prevScreen);
    }
  };

  const goToScreen = (screenIndex: number) => {
    if (screenIndex >= 0 && screenIndex < screens.length) {
      updateUrl(screenIndex);
    }
  };

  const isFirstScreen = currentScreen === 0;
  const isLastScreen = currentScreen === screens.length - 1;

  // Initialize URL with step param if not present on mount
  // Only run after router is ready to avoid overwriting existing step param
  useEffect(() => {
    if (!router.isReady) return;

    // Check if there's actually no step in the URL (not just in router.query)
    const hasStepInUrl = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('step');
    if (!hasStepInUrl && !stepFromQuery && screens[currentScreen]?.id) {
      updateUrl(currentScreen);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady]);

  const navigationProps: WizardNavigationProps = {
    goToNextScreen,
    goToPreviousScreen,
    goToScreen,
    currentScreen,
    totalScreens: screens.length,
    isFirstScreen,
    isLastScreen,
  };

  const getDefaultFooter = () => {
    const backButtonText = currentScreenData?.backButtonText ?? defaultBackButtonText;
    const nextButtonText = currentScreenData?.nextButtonText ?? defaultNextButtonText;

    // Determine if next button should be enabled
    let isNextButtonEnabled = true;

    // If screen has a nextRoute or nextAction, check isNextEnabled if provided
    if (currentScreenData?.nextRoute || currentScreenData?.nextAction) {
      if (currentScreenData?.isNextEnabled !== undefined) {
        if (typeof currentScreenData.isNextEnabled === 'function') {
          isNextButtonEnabled = currentScreenData.isNextEnabled();
        } else {
          isNextButtonEnabled = currentScreenData.isNextEnabled;
        }
      }
    } else {
      // Default wizard navigation logic
      isNextButtonEnabled = !isLastScreen;
      if (currentScreenData?.isNextEnabled !== undefined) {
        if (typeof currentScreenData.isNextEnabled === 'function') {
          isNextButtonEnabled = isNextButtonEnabled && currentScreenData.isNextEnabled();
        } else {
          isNextButtonEnabled = isNextButtonEnabled && currentScreenData.isNextEnabled;
        }
      }
    }

    return (
      <>
        {currentScreen > 0 && (
          <Button
            onClick={goToPreviousScreen}
            disabled={isFirstScreen}
            tone="neutral"
            data-testid="back-button"
            css={{
              display: 'flex',
              flex: 1,
              marginRight: '{spacing.400}',
              '@media (min-width: 1024px)': {
                display: 'block',
                flex: 'none',
                marginRight: '0',
                minWidth: '164px',
                textAlign: 'center',
              },
            }}
          >
            {backButtonText}
          </Button>
        )}
        <Button
          onClick={goToNextScreen}
          disabled={!isNextButtonEnabled}
          loading={currentScreenData?.isLoading}
          tone="brand"
          emphasis="filled"
          data-testid="next-button"
          css={{
            display: 'flex',
            flex: 1,
            '@media (min-width: 1024px)': {
              display: 'block',
              flex: 'none',
              marginLeft: currentScreen > 0 ? '0' : 'auto',
              minWidth: '164px',
              textAlign: 'center',
            },
          }}
        >
          {nextButtonText}
        </Button>
      </>
    );
  };

  return (
    <Modal.Root size="fullScreen" open={!!true}>
      <Modal.Portal>
        <Modal.Backdrop data-panda-theme="zillow" css={{ animation: 'none' }}>
          <Modal.Content css={{ animation: 'none' }}>
            {headerToShow ? (
              <>
                <Modal.Header
                  css={{ paddingLeft: { base: '16px', lg: '96px' }, paddingRight: { base: '16px', lg: '96px' } }}
                >
                  {headerToShow}
                </Modal.Header>
                <Divider />
              </>
            ) : (
              <Modal.Header css={{ display: 'none' }}>{null}</Modal.Header>
            )}
            <Modal.Body css={{ padding: { base: '{spacing.default}', lg: '32px 96px 32px 160px' } }}>
              {error && (
                <Box css={{ marginBottom: '{spacing.600}' }}>
                  <Alert.Root tone="critical" data-testid="error-alert">
                    <Alert.Body>{error}</Alert.Body>
                    {onDismissError && (
                      <Alert.Close>
                        <CloseButton onClick={onDismissError} />
                      </Alert.Close>
                    )}
                  </Alert.Root>
                </Box>
              )}
              {currentScreenData?.body}
            </Modal.Body>
            {currentScreenData?.showFooter !== false && (
              <>
                <Divider />
                <Modal.Footer
                  css={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    paddingLeft: { base: '{spacing.default}', lg: '{spacing.2400}' },
                    paddingRight: { base: '{spacing.default}', lg: '{spacing.2400}' },
                    paddingBottom: { base: '{spacing.default}', lg: 'auto' },
                  }}
                >
                  {currentScreenData?.footer
                    ? typeof currentScreenData.footer === 'function'
                      ? currentScreenData.footer(navigationProps)
                      : currentScreenData.footer
                    : getDefaultFooter()}
                </Modal.Footer>
              </>
            )}
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
}
